import { useLocation } from 'react-router-dom';

import { MAIL_AQUA_SECURITY } from 'constants/emails';
import { AppRoutes } from 'constants/routes';
import { AQUA_DOCS_AUDIT, AQUA_DOCS_URL } from 'constants/urls';

import { normalizePath } from 'helpers/url';

import Audit from 'assets/icons/objects/icon-audit-16.svg';
import Docs from 'assets/icons/objects/icon-docs-16x17.svg';

import { BlankRouterLink } from 'basics/links';

import ThemeSwitch from 'components/ThemeSwitch/ThemeSwitch';

import {
    FooterBlock,
    Wrapper,
    LogoWithDesc,
    Description,
    LinksAndCopyright,
    ExchangesAndCopyright,
    Exchanges,
    Aqua,
    DocLinks,
    AquaLinks,
    AquaLink,
    MailLink,
    DocsLink,
    DuneLogo,
    CompactFooter,
    CompactFooterContent,
    FooterControls,
} from './Footer.styled';

const WIDE_PAGES = [
    AppRoutes.page.vote,
    AppRoutes.page.account,
    AppRoutes.page.terms,
    AppRoutes.page.privacy,

    AppRoutes.section.bribes.base,
    AppRoutes.section.locker.base,
    AppRoutes.section.governance.base,
    AppRoutes.section.market.base,
    AppRoutes.section.amm.base,
];

const PAGES_WITH_COMPACT_FOOTER = [AppRoutes.section.swap.base];

const PAGES_WITH_COMPACT_FOOTER_EXACT = [AppRoutes.section.locker.link.index];

const Footer = (): React.ReactNode => {
    const location = useLocation();

    if (
        PAGES_WITH_COMPACT_FOOTER.some(page => normalizePath(location.pathname).startsWith(page)) ||
        PAGES_WITH_COMPACT_FOOTER_EXACT.some(
            page => normalizePath(location.pathname) === normalizePath(page),
        )
    ) {
        return (
            <CompactFooter>
                <CompactFooterContent>
                    <ThemeSwitch />
                </CompactFooterContent>
            </CompactFooter>
        );
    }

    const isWidePage = WIDE_PAGES.includes(normalizePath(location.pathname) as string);

    return (
        <FooterBlock>
            <Wrapper $isWide={isWidePage}>
                <LogoWithDesc>
                    <BlankRouterLink to={AppRoutes.page.main}>
                        <Aqua />
                    </BlankRouterLink>
                    <Description>
                        Aquarius runs on Stellar. AQUA tokens are issued on Stellar. <br />
                        The project is unaffiliated with the Stellar Development Foundation.
                    </Description>
                    <AquaLinks>
                        <AquaLink to={AppRoutes.page.terms}>Terms of use</AquaLink>
                        <AquaLink to={AppRoutes.page.privacy}>Privacy policy</AquaLink>
                    </AquaLinks>
                </LogoWithDesc>
                <LinksAndCopyright>
                    <DocLinks>
                        <DocsLink href="https://dune.com/claw/aquarius-stellar" target="_blank">
                            <DuneLogo />
                            Dune dashboard
                        </DocsLink>
                        <DocsLink href={AQUA_DOCS_URL} target="_blank">
                            <Docs />
                            Aquarius docs
                        </DocsLink>
                        <DocsLink href={AQUA_DOCS_AUDIT} target="_blank">
                            <Audit />
                            Audits
                        </DocsLink>
                    </DocLinks>
                    <ExchangesAndCopyright>
                        <Exchanges>
                            Security reports:{' '}
                            <MailLink
                                href={`mailto:${MAIL_AQUA_SECURITY}`}
                                target="_blank"
                                rel="noreferrer noopener"
                            >
                                {MAIL_AQUA_SECURITY}
                            </MailLink>
                        </Exchanges>
                        <FooterControls>
                            <span>© {new Date().getFullYear()} aqua.network</span>
                            <ThemeSwitch />
                        </FooterControls>
                    </ExchangesAndCopyright>
                </LinksAndCopyright>
            </Wrapper>
        </FooterBlock>
    );
};

export default Footer;
